import * as React from "react";
import { Spinner } from "@fluentui/react-components";
import { initializePaddle, Paddle, PaddleEventData } from '@paddle/paddle-js';
import { useNavigate, useSearchParams } from "react-router-dom";
import { CenteredContent } from "../../components/CenteredContent";
import { usePaddleClientSideToken, usePaddleEnv } from "../../util/PaddleEnv";

export interface CheckoutProps
{
}

export const Checkout: React.FC<CheckoutProps> = () =>
{
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const paddleEnv = usePaddleEnv();
    const paddleClientSideToken = usePaddleClientSideToken();
    const initialUrl = React.useRef(window.location.href);

    const eventCallback = React.useCallback((event: PaddleEventData) =>
    {
        if (event.name === "checkout.closed")
        {
            if (window.location.href === initialUrl.current)
            {
                navigate(-1);
            }
        }
    }, [navigate]);

    // Create a local state to store Paddle instance
    const [paddle, setPaddle] = React.useState<Paddle>();

    // Close paddle on leaving of the checkout page to not stay open if popup is not closed but navigated back
    React.useEffect(() =>
    {
        return () =>
        {
            if (paddle?.Checkout)
            {
                paddle.Checkout.close();
            }
        };
    }, [paddle]);

    // Download and initialize Paddle instance from CDN
    React.useEffect(() =>
    {
        initializePaddle({
            environment: paddleEnv,
            token: paddleClientSideToken,
            eventCallback: eventCallback
        })
            .then((paddleInstance: Paddle | undefined) =>
            {
                if (paddleInstance)
                {
                    setPaddle(paddleInstance);
                }
            });
    }, [eventCallback, paddleClientSideToken, paddleEnv]);

    React.useEffect(() => {
        const transactionId = searchParams.get("_ptxn");
        const priceId = searchParams.get("priceId");
        const referenceId = searchParams.get("referenceId");

        if (!paddle)
        {
            return;
        }

        if (transactionId)
        {
            paddle.Checkout.open({
                settings: {
                    allowLogout: false,
                    theme: "dark"
                },
                transactionId
            });
        }
        else if (priceId)
        {
            paddle.Checkout.open({
                settings: {
                    // Prevent user from changing their email
                    allowLogout: false,
                    theme: "dark"
                },
                items: [{ priceId, quantity: 1 }],
                // maybe set at a later point: customer
                customData: {
                    referenceId
                }
            });
        }
        else
        {
            navigate("/");
        }
    }, [navigate, paddle, searchParams]);

    return (
        <CenteredContent>
            { paddle == null && <Spinner /> }
        </CenteredContent>
    )
};
